import { useCallback, useEffect, useRef, useState } from 'react';
import { getGuidedCareJourneySnapshot } from '../../services/guidedCareJourneyService';

const useProgressiveJourneySnapshot = (childId, {
  journeyId = 'iep_meeting', draftId = 'iep_meeting-v3-current',
} = {}) => {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enhancing, setEnhancing] = useState(false);
  const [enhancementError, setEnhancementError] = useState(false);
  const [error, setError] = useState('');
  const requestIdRef = useRef(0);

  const enhanceSnapshot = useCallback(async (requestId) => {
    setEnhancing(true);
    setEnhancementError(false);
    try {
      const enhanced = await getGuidedCareJourneySnapshot({ childId, journeyId, draftId, enhance: true });
      if (requestIdRef.current === requestId) setSnapshot(enhanced);
      return enhanced;
    } catch (_error) {
      // Keep the deterministic Journey usable when the optional AI refresh fails.
      if (requestIdRef.current === requestId) setEnhancementError(true);
      return null;
    } finally {
      if (requestIdRef.current === requestId) setEnhancing(false);
    }
  }, [childId, draftId, journeyId]);

  const refreshSnapshot = useCallback(async () => {
    if (!childId) return;
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setError('');
    try {
      const baseline = await getGuidedCareJourneySnapshot({ childId, journeyId, draftId, enhance: false });
      if (requestIdRef.current !== requestId) return;
      setSnapshot(baseline);
      setLoading(false);
      if (baseline?.enhancementCache?.status !== 'hit') return enhanceSnapshot(requestId);
      return baseline;
    } catch (requestError) {
      if (requestIdRef.current !== requestId) return;
      if (!snapshot) setError(requestError?.message || 'This journey is not available right now.');
      setLoading(false);
      return null;
    }
  }, [childId, draftId, enhanceSnapshot, journeyId, snapshot]);

  const retryEnhancement = useCallback(() => {
    if (!childId || enhancing) return;
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    void enhanceSnapshot(requestId);
  }, [childId, enhanceSnapshot, enhancing]);

  useEffect(() => {
    setSnapshot(null);
    setLoading(true);
    setEnhancing(false);
    setEnhancementError(false);
    setError('');
    void refreshSnapshot();
    return () => { requestIdRef.current += 1; };
  // refreshSnapshot changes when the current snapshot changes; childId is the load boundary.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childId]);

  return {
    snapshot,
    setSnapshot,
    loading,
    enhancing,
    enhancementError,
    error,
    refreshSnapshot,
    retryEnhancement,
  };
};

export default useProgressiveJourneySnapshot;
